import axios from 'axios';

let API_URL = process.env.VUE_APP_API_BASE_URL || 'http://localhost:3003/api';

// Fallback to append /api if it was mistakenly omitted in .env
if (API_URL && !API_URL.endsWith('/api') && API_URL.includes('localhost:3003')) {
  API_URL = `${API_URL.replace(/\/$/, '')}/api`;
}

const DEEPCOREX_URL = process.env.VUE_APP_DEEPCOREX_URL || 'http://localhost:5101';

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor for API calls
axiosInstance.interceptors.request.use(
  async config => {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('api:start'));
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('api:end'));
    Promise.reject(error);
  }
);

// Response interceptor for API calls
axiosInstance.interceptors.response.use((response) => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('api:end'));
  return response;
}, async function (error) {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('api:end'));
  const originalRequest = error.config;
  if (error.response?.status === 401 && !originalRequest._retry) {
    // Handle token expiration or unauthorized access
    localStorage.removeItem('access_token');
    
    // Redirect to DeepCoreX login
    if (window.location.pathname !== '/login' && window.location.pathname !== '/auth/callback') {
        window.location.href = `${DEEPCOREX_URL}/login?redirect=deeptrack`;
    }
    return Promise.reject(error);
  }
  return Promise.reject(error);
});

export const api = {
  auth: {
    login: async (credentials) => {
      const response = await axiosInstance.post('/auth/login', credentials);
      if (response.data.session?.access_token) {
        localStorage.setItem('access_token', response.data.session.access_token);
      }
      return response.data;
    },
    signup: async (userData) => {
      const response = await axiosInstance.post('/auth/signup', userData);
      const token = response.data.token || response.data.session?.access_token;
      if (token) {
        localStorage.setItem('access_token', token);
      }
      return response.data;
    },
    getMe: async () => {
        try {
            const response = await axiosInstance.get('/auth/me');
            return response.data;
        } catch (e) {
            return null;
        }
    },
    logout: async () => {
      localStorage.removeItem('access_token');
      return Promise.resolve();
    },
    resetPassword: async () => {
        // Implement if backend supports it
        // return axiosInstance.post('/auth/reset-password', { email });
        console.warn('Reset password not fully implemented in backend');
        return { error: null }; 
    },
    updateUser: async (data) => {
        // Implement if backend supports it
         return axiosInstance.patch('/auth/me', data);
    }
  },
  data: {
    list: async (resource, params = {}) => {
      const response = await axiosInstance.get(`/data/${resource}`, { params });
      return response.data;
    },
    get: async (resource, id) => {
      const response = await axiosInstance.get(`/data/${resource}/${id}`);
      return response.data;
    },
    create: async (resource, data) => {
      const response = await axiosInstance.post(`/data/${resource}`, data);
      return response.data;
    },
    update: async (resource, id, data) => {
      const response = await axiosInstance.patch(`/data/${resource}/${id}`, data);
      return response.data;
    },
    delete: async (resource, id) => {
      const response = await axiosInstance.delete(`/data/${resource}/${id}`);
      return response.data;
    }
  },
  storage: {
    // Direct-to-R2 upload via a server-signed PUT URL. Avoids the Cloud Run
    // request-body limit that broke large multipart POSTs through multer.
    upload: async (bucket, file, filePath = null) => {
      const path = filePath || file.name;
      const { data: sig } = await axiosInstance.post('/storage/sign-upload', { bucket, filePath: path });
      await axios.put(sig.uploadUrl, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        transformRequest: [(d) => d],
      });
      return { path: sig.path, fullPath: sig.path, url: sig.path };
    },
    getPublicUrl: (bucket, path) => {
        // Helper to construct URL
        const cleanPath = path.startsWith('/') ? path.slice(1) : path;
        const clientId = process.env.VUE_APP_DEFAULT_CLIENT_ID || 'default';
        return `${API_URL.replace('/api', '')}/uploads/${clientId}/${bucket}/${cleanPath}`;
    },
    remove: async (bucket, paths) => {
        const clientId = process.env.VUE_APP_DEFAULT_CLIENT_ID || 'default';
        const response = await axiosInstance.post(`/storage/${clientId}/${bucket}/remove`, paths);
        return response.data;
    },
    download: async (bucket, path) => {
         const clientId = process.env.VUE_APP_DEFAULT_CLIENT_ID || 'default';
         const response = await axiosInstance.get(`/storage/${clientId}/${bucket}/${path}`, { responseType: 'blob' });
         return response.data;
    }
  },
  functions: {
    invoke: async (functionName, body) => {
      const response = await axiosInstance.post(`/functions/${functionName}`, body);
      return response.data;
    }
  },
  // Procurement business-logic endpoints (GRN, 4-way match, payment approval)
  procurement: {
    createGRN: async (data) => (await axiosInstance.post('/procurement/grn', data)).data,
    verifyDO: async (id) => (await axiosInstance.post(`/procurement/do/${id}/verify`)).data,
    rejectDO: async (id, reason) => (await axiosInstance.post(`/procurement/do/${id}/reject`, { reason })).data,
    requestPayment: async (data) => (await axiosInstance.post('/procurement/request-payment', data)).data,
    listPayments: async () => (await axiosInstance.get('/procurement/payments')).data,
    myPendingPayments: async () => (await axiosInstance.get('/procurement/payments/my-pending')).data,
    getPayment: async (id) => (await axiosInstance.get(`/procurement/payment/${id}`)).data,
    approvePayment: async (id) => (await axiosInstance.post(`/procurement/payment/${id}/approve`)).data,
    rejectPayment: async (id, reason) => (await axiosInstance.post(`/procurement/payment/${id}/reject`, { reason })).data,
    tracking: async (prId) => (await axiosInstance.get(`/procurement/pr/${prId}/tracking`)).data,
  },
  // Zoho read-only proxy (PO / invoice / payment data linked by PR code)
  zoho: {
    status: async () => (await axiosInstance.get('/zoho/status')).data,
    posByPR: async (prCode) => (await axiosInstance.get(`/zoho/po/by-pr/${encodeURIComponent(prCode)}`)).data,
    po: async (id) => (await axiosInstance.get(`/zoho/po/${id}`)).data,
    invoices: async (poId) => (await axiosInstance.get(`/zoho/po/${poId}/invoices`)).data,
    payments: async (poId) => (await axiosInstance.get(`/zoho/po/${poId}/payments`)).data,
    vendor: async (id) => (await axiosInstance.get(`/zoho/vendor/${id}`)).data,
    vendors: async () => (await axiosInstance.get('/zoho/vendors')).data,
  },
  // Generic HTTP methods for custom endpoints
  post: async (endpoint, data) => {
    const response = await axiosInstance.post(endpoint, data);
    return response.data;
  },
  get: async (endpoint) => {
    const response = await axiosInstance.get(endpoint);
    return response.data;
  }
};

export default api;
